import { useEffect, useMemo, useState } from "react";
import { BadgeCheck, CheckCircle2, Loader2, Search, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { authedFetch } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const AdminNameCorrections = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sort, setSort] = useState("oldest");

  const load = async () => {
    const res = await authedFetch("/admin/name-corrections");
    setList(res.ok ? await res.json() : []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const decide = async (item, action) => {
    const id = item.id;
    setActing(id + action);
    const base = item.source_type === "ACCOUNT" ? "/admin/account-name-corrections" : "/admin/name-corrections";
    const res = await authedFetch(base + "/" + id + "/" + action, { method: "POST" });
    setActing(null);
    if (!res.ok) {
      toast.error("Could not process the name correction.");
      return;
    }
    toast.success(action === "approve" ? "Name correction approved." : "Name correction rejected.");
    setList((items) => items.filter((item) => item.id !== id));
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const now = new Date();
    return list.filter((item) => {
      const matches = !needle || JSON.stringify(item).toLowerCase().includes(needle);
      if (dateRange === "all") return matches;
      const date = new Date(item.name_correction_requested_at || 0);
      if (dateRange === "today") return matches && date.toDateString() === now.toDateString();
      return matches && date >= new Date(now.getTime() - Number(dateRange) * 86400000);
    }).sort((a, b) => {
      if (sort === "name") return (a.last_name || "").localeCompare(b.last_name || "");
      const ad = new Date(a.name_correction_requested_at || 0);
      const bd = new Date(b.name_correction_requested_at || 0);
      return sort === "oldest" ? ad - bd : bd - ad;
    });
  }, [list, query, dateRange, sort]);

  return (
    <section id="name-corrections" className="scroll-mt-28" data-testid="admin-name-corrections-section">
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-5 w-5 text-primary" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Name correction requests</h2>
        <span className="ml-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{visible.length} pending</span>
      </div>
      <p className="mt-1 text-sm text-slate-500">Review requested account-name changes. Clinician requests can be checked against their AHPRA registration.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => setSearchOpen(!searchOpen)} aria-label="Search name corrections">{searchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}</Button>
        {searchOpen && <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or AHPRA…" className="w-64" />}
        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="all">Any date</option><option value="today">Today</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option></select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"><option value="oldest">Oldest first</option><option value="newest">Newest first</option><option value="name">Name A–Z</option></select>
      </div>

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : visible.length === 0 ? (
        <div className="mt-5 rounded-md border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No name correction requests.</p>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {visible.map((c) => (
            <div key={c.id} className="rounded-md border border-slate-200 bg-white p-5" data-testid={"admin-name-correction-" + c.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Current name</p>
                  <p className="font-semibold text-slate-900">{c.first_name} {c.last_name}</p>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">Requested name</p>
                  <p className="font-semibold text-primary">{c.requested_first_name} {c.requested_last_name}</p>
                </div>
                {c.source_type === "CLINICIAN" && (
                  <a href="https://www.ahpra.gov.au/Registration/Registers-of-Practitioners.aspx" target="_blank" rel="noreferrer" className="rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-primary hover:bg-slate-50">
                    Check register ↗
                  </a>
                )}
              </div>
              {c.source_type === "CLINICIAN" && (
                <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  <span className="font-medium text-slate-700">AHPRA number:</span> {c.ahpra_number || "Not supplied"}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-500">Submitted {c.name_correction_requested_at ? new Date(c.name_correction_requested_at).toLocaleString() : "date unavailable"}</p>
              {c.name_correction_reason && <p className="mt-3 text-sm text-slate-600"><span className="font-medium">Note:</span> {c.name_correction_reason}</p>}
              <div className="mt-4 flex gap-2 border-t border-slate-100 pt-4">
                <Button size="sm" disabled={!!acting} onClick={() => decide(c, "approve")}>
                  {acting === c.id + "approve" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Approve
                </Button>
                <Button size="sm" variant="outline" disabled={!!acting} onClick={() => decide(c, "reject")}>
                  {acting === c.id + "reject" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />} Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
